import { API_BASE, ENVIRONMENT_TYPE } from '../../config/env';
import { ORCHESTRATOR_CONFIG } from '../../config/orchestratorConfig';
import { httpConstants } from '../../constants';
import { httpService } from '../../utility/httpService';
import { createLabelWithUsername } from '../../utility/userUtils';

export interface ChatQuery {
  report?: string;
  impact?: string;
  user_input?: string;
  user_query?: string;
  user_file_data?: Record<string, any>;
}

export interface ChatMessage {
  id: string;
  query: ChatQuery;
  answer?: string;
  timeTaken: number;
  metadata?: any;
  status?: string;
  files?: {
    data: Record<string, any>[];
  };
  created_at?: string;
  updated_at?: string;
  reportId?: string;
  report?: Record<string, any>;
  discoveryContent?: Record<string, any>;
  version?: string;
  impact?: string;
  fileSummary?: string;
  context_knowledge?: {
    data: Record<string, string>[];
  };
  semantic_knowledge?: Record<string, any>;
  tokenUsage?: Record<string, any>;
  username?: string;
}

export interface ChatSession {
  data: ChatMessage[];
  total: number;
}

export interface VersionData {
  [version: string]: any;
}

export interface QueryHistoryItem {
  id: string;
  use_case_id: string;
  use_case_title: string;
  field_type: string;
  version: string;
  semantic_knowledge: {
    report: string | null;
    file_summary: string;
  };
  created_at: string;
  updated_at: string;
}

export interface QueryHistoryResponse {
  data: QueryHistoryItem[];
  total: number;
}

export async function getLatestQueryHistory(
  limit: number = 10,
  organization_id: string,
  fieldType?: string,
  showOnlyReports: boolean = true,
  session_name_filter?: string,
  sortBy: string = '',
  conversation_type?: string,
  page?: number
): Promise<QueryHistoryResponse> {
  let url = `${API_BASE}/api/v1/ideation/query-history/latest/?limit=${limit}&organization_id=${organization_id}&sortBy=${sortBy}`;
  if (page) {
    url += `&page=${page}`;
  }

  if (session_name_filter) {
    url += `&session_name=${session_name_filter}`;
  }

  // Only include field_type when a specific field is requested (not 'All')
  if (fieldType && fieldType !== 'All' && !session_name_filter) {
    url += `&field_type=${fieldType}`;
  }
  url += `&showOnlyReports=${showOnlyReports}`;

  if (conversation_type) {
    url += `&conversation_type=${conversation_type}`;
  }

  try {
    const response = await httpService<QueryHistoryResponse>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch query history:', error);
    throw error;
  }
}

export async function getUseCaseDetails(usecaseId: string): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/use-cases/${usecaseId}/`;

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}

// AFTER
export async function getChatHistory(
  usecaseId: string,
  field?: string,
  conversation_type?: string
): Promise<any> {
  const organization_id = window?.localStorage?.getItem('current_organization') || '';
  let url = `${API_BASE}/api/v1/ideation/query-history/${usecaseId}/?skip=0&limit=1000&sortingKey=created_at&sortingOrder=1&organization_id=${organization_id}`;

  if (field) {
    url += `&field_type=${field}`;
  }

  if (conversation_type) {
    url += `&conversation_type=${conversation_type}`;
  }

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}

export async function postQuery(payload: any): Promise<any | undefined> {
  const url = `${API_BASE}/api/v1/ideation/query-use-case/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload);
    console.log('Tech Landscape Diagram Response:', response);
    return response;
  } catch (error) {
    console.error('Failed to fetch tech landscape diagram:', error);
    return undefined;
  }
}

export async function editContent(payload: any): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/query-use-case/${payload.useCaseId}/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PUT, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to edit content:', error);
    return error;
  }
}

export async function updateDraftStatus(useCaseId: string, payload: any): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/use-cases/${useCaseId}/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to edit content:', error);
    return error;
  }
}

export const analyzeFile = async (
  fileUrl: string,
  fileName: string,
  fileId: string,
  fileSize: string
) => {
  try {
    const url = ORCHESTRATOR_CONFIG.ENDPOINTS.ANALYZE_FILE;

    const payload = {
      files: [
        {
          id: fileId,
          fileUrl,
          fileName,
          fileData: '',
          fileSize,
          fileSummary: '',
          env: ENVIRONMENT_TYPE,
        },
      ],
      label: createLabelWithUsername(fileId),
      organization_id: window?.localStorage?.getItem('current_organization') || '',
    };

    const result = await httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload);

    return result;
  } catch (error) {
    console.error('Error analyzing file:', error);
    throw error;
  }
};

export async function getPreviousSessions(useCaseId: string): Promise<any> {
  const url = `https://api.zbrain.ai/contentms/chat/sessions/${useCaseId}?appId=6914205061cec0b268eba06b&tenantId=6764029052789d00255f17a1`;

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}
export async function createIdeationIntake(payload: {
  use_case_id: string;
  conversation_ids: string[];
  type: string;
  organization_id: string;
  created_by_name: string;
  versions: string[];
}): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/intake/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to create ideation intake:', error);
    throw error;
  }
}

export async function getSessionStatus(
  organization_id: string,
  useCaseId: string,
  conversation_type?: string
): Promise<any> {
  let url = `${API_BASE}/api/v1/ideation/query-endpoint-status/?organization_id=${organization_id}&use_case_id=${useCaseId}`;

  if (conversation_type) {
    url += `&conversation_type=${conversation_type}`;
  }

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}

export async function renameSession(
  chatId: string,
  sessionName: string,
  fieldType: string,
  conversation_type?: string
): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/query-history/${chatId}/session-name/`;
  const payload: Record<string, any> = {
    session_name: sessionName,
    field_type: fieldType,
    organization_id: window?.localStorage?.getItem('current_organization') || '',
  };
  if (conversation_type) {
    payload.conversation_type = conversation_type;
  }
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
    return response;
  } catch (error) {
    console.error('Error renaming session:', error);
    throw error;
  }
}

export async function deleteSession(
  chatId: string,
  fieldType: string,
  conversation_type?: string
): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/query-history/${chatId}/delete/`;
  const payload: Record<string, any> = {
    field_type: fieldType,
    organization_id: window?.localStorage?.getItem('current_organization') || '',
  };
  if (conversation_type) {
    payload.conversation_type = conversation_type;
  }
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url, payload);
    return response;
  } catch (error) {
    console.error('Error deleting session:', error);
    throw error;
  }
}

export async function getIdeationIntakes(
  use_case_id: string,
  organization_id: string
): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/intake/?use_case_id=${use_case_id}&organization_id=${organization_id}`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch ideation intakes:', error);
    return { data: [], total: 0 };
  }
}

export async function generateDiagramXML(
  payload: Record<string, string>
): Promise<Record<string, string>> {
  const url = `${API_BASE}/api/v1/ideation/orchestrator/diagram-generation/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload);
    return response;
  } catch (error) {
    console.error('Error deleting session:', error);
    throw error;
  }
}

export async function getDiagramStatus(params: {
  conversation_id: string;
  organization_id: string;
  conversation_type: string;
  field_type: string;
}): Promise<any> {
  const { conversation_id, organization_id, conversation_type, field_type } = params;
  const url = `${API_BASE}/api/v1/ideation/diagram-status/?conversation_id=${conversation_id}&organization_id=${organization_id}&conversation_type=${conversation_type}&field_type=${field_type}`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Error fetching diagram status:', error);
    throw error;
  }
}
